import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPieChart, FiBarChart2, FiTrendingUp, FiDollarSign, FiTarget, FiCalendar, FiArrowRight, FiDownload, FiArrowUp, FiArrowDown } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

const ConsumptionInsights: React.FC = () => {
  const [activeTimeframe, setActiveTimeframe] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Mock data
  const spendingCategories = [
    { id: 1, name: 'Housing', amount: 1200, percentage: 40, color: 'bg-primary' },
    { id: 2, name: 'Food', amount: 600, percentage: 20, color: 'bg-secondary' },
    { id: 3, name: 'Transportation', amount: 300, percentage: 10, color: 'bg-accent' },
    { id: 4, name: 'Entertainment', amount: 300, percentage: 10, color: 'bg-success' },
    { id: 5, name: 'Shopping', amount: 450, percentage: 15, color: 'bg-purple-500' },
    { id: 6, name: 'Other', amount: 150, percentage: 5, color: 'bg-border' },
  ];
  
  const spendingHabits = [
    { id: 1, title: "Coffee Shops", frequency: "12x per month", amount: 60, insight: "Morning pattern detected" },
    { id: 2, title: "Subscriptions", frequency: "5 active services", amount: 85, insight: "2 rarely used" },
    { id: 3, title: "Dining Out", frequency: "8x per month", amount: 320, insight: "Weekend pattern" },
    { id: 4, title: "Rideshare", frequency: "15x per month", amount: 200, insight: "Weekday commuting" },
  ];
  
  const trends = [
    { id: 1, title: "Grocery Spending", change: -15, insight: "Decreasing trend", isPositive: true },
    { id: 2, title: "Food Delivery", change: 28, insight: "Increasing trend", isPositive: false },
    { id: 3, title: "Utility Bills", change: 8, insight: "Seasonal increase", isPositive: false },
    { id: 4, title: "Subscription Services", change: -10, insight: "Reduced services", isPositive: true },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Consumption Insights</h1>
            <p className="text-text/70 max-w-3xl">
              Our AI analyzes your spending patterns and provides personalized insights to help you make smarter financial decisions.
            </p>
          </div>

          {/* Time Selector */}
          <div className="mb-8">
            <div className="inline-flex p-1 border border-border rounded-lg bg-card">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium ${activeTimeframe === 'month' ? 'bg-primary text-white' : 'text-text/70 hover:text-text'}`}
                onClick={() => setActiveTimeframe('month')}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium ${activeTimeframe === 'quarter' ? 'bg-primary text-white' : 'text-text/70 hover:text-text'}`}
                onClick={() => setActiveTimeframe('quarter')}
              >
                Quarterly
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium ${activeTimeframe === 'year' ? 'bg-primary text-white' : 'text-text/70 hover:text-text'}`}
                onClick={() => setActiveTimeframe('year')}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto gap-2 mb-8 border-b border-border">
            <button 
              className={`px-4 py-3 font-medium hover:text-primary transition-colors ${activeTab === 'overview' ? 'text-primary border-b-2 border-primary' : 'text-text/70'}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`px-4 py-3 font-medium hover:text-primary transition-colors ${activeTab === 'habits' ? 'text-primary border-b-2 border-primary' : 'text-text/70'}`}
              onClick={() => setActiveTab('habits')}
            >
              Spending Habits
            </button>
            <button 
              className={`px-4 py-3 font-medium hover:text-primary transition-colors ${activeTab === 'trends' ? 'text-primary border-b-2 border-primary' : 'text-text/70'}`}
              onClick={() => setActiveTab('trends')}
            >
              Trends & Predictions
            </button>
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Spending Breakdown Card */}
                <Card variant="glass" className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold">Monthly Spending Breakdown</h2>
                      <p className="text-text/70">Total: $3,000</p>
                    </div>
                    <Button variant="outline" size="sm" rightIcon={<FiDownload size={14} />}>
                      Export Data
                    </Button>
                  </div>
                  
                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="lg:w-1/2">
                      <div className="relative w-full h-64 flex items-center justify-center">
                        <div className="w-48 h-48 rounded-full relative">
                          {spendingCategories.map((category, index) => (
                            <div 
                              key={category.id} 
                              className={`absolute w-48 h-48 rounded-full ${category.color}`}
                              style={{
                                clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.cos((index - 0.5) * 2 * Math.PI / spendingCategories.length)}% ${50 + 50 * Math.sin((index - 0.5) * 2 * Math.PI / spendingCategories.length)}%, ${50 + 50 * Math.cos(index * 2 * Math.PI / spendingCategories.length)}% ${50 + 50 * Math.sin(index * 2 * Math.PI / spendingCategories.length)}%)`
                              }}
                            ></div>
                          ))}
                          <div className="absolute inset-0 flex items-center justify-center w-20 h-20 bg-card rounded-full m-auto">
                            <span className="font-semibold text-lg">$3,000</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="lg:w-1/2">
                      <div className="space-y-3">
                        {spendingCategories.map((category) => (
                          <div key={category.id} className="flex items-center">
                            <div className={`w-3 h-3 rounded-full ${category.color} mr-3`}></div>
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <span className="font-medium">{category.name}</span>
                                <span>${category.amount}</span>
                              </div>
                              <div className="w-full h-1.5 bg-border rounded-full mt-1">
                                <div 
                                  className={`h-full ${category.color} rounded-full`} 
                                  style={{ width: `${category.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
                
                {/* AI Insights Card */}
                <Card variant="glass" className="p-6">
                  <h2 className="text-xl font-semibold mb-6">AI-Generated Insights</h2>
                  
                  <div className="space-y-4">
                    <div className="p-4 border border-border rounded-lg bg-card/60">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                          <FiTrendingUp className="text-primary" size={20} />
                        </div>
                        <div>
                          <h3 className="font-medium">Spending Pattern Detected</h3>
                          <p className="text-sm text-text/70">Food & Dining</p>
                        </div>
                      </div>
                      <p className="mb-2">
                        You spend 35% more on food delivery during weekdays compared to weekends. 
                        Setting a weekday food budget could save you approximately $120/month.
                      </p>
                      <Button size="sm" variant="outline">View Spending Pattern</Button>
                    </div>
                    
                    <div className="p-4 border border-border rounded-lg bg-card/60">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center mr-3">
                          <FiTarget className="text-secondary" size={20} />
                        </div>
                        <div>
                          <h3 className="font-medium">Budget Optimization</h3>
                          <p className="text-sm text-text/70">Entertainment & Subscriptions</p>
                        </div>
                      </div>
                      <p className="mb-2">
                        You have 5 active subscription services totaling $85/month, but usage data shows 
                        you rarely use 2 of them ($30/month). Consider canceling these services.
                      </p>
                      <Button size="sm" variant="outline">Review Subscriptions</Button>
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Sidebar */}
              <div className="space-y-6">
                {/* Financial Summary Card */}
                <Card variant="glass" className="p-6">
                  <h2 className="text-xl font-semibold mb-6">Financial Summary</h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-border">
                      <div>
                        <p className="text-sm text-text/70">Monthly Income</p>
                        <p className="text-xl font-semibold">$4,500</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <FiArrowUp className="text-green-500" size={20} />
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-border">
                      <div>
                        <p className="text-sm text-text/70">Monthly Expenses</p>
                        <p className="text-xl font-semibold">$3,000</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                        <FiArrowDown className="text-red-500" size={20} />
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3">
                      <div>
                        <p className="text-sm text-text/70">Monthly Savings</p>
                        <p className="text-xl font-semibold">$1,500</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <FiDollarSign className="text-primary" size={20} />
                      </div>
                    </div>
                  </div>
                </Card>
                
                {/* Quick Actions Card */}
                <Card variant="glass" className="p-6">
                  <h2 className="text-xl font-semibold mb-6">Quick Actions</h2>
                  
                  <div className="space-y-3">
                    <Button variant="outline" fullWidth className="justify-start" leftIcon={<FiCalendar className="mr-2" />}>
                      Set Monthly Budget
                    </Button>
                    <Button variant="outline" fullWidth className="justify-start" leftIcon={<FiTarget className="mr-2" />}>
                      Create Savings Goal
                    </Button>
                    <Button variant="outline" fullWidth className="justify-start" leftIcon={<FiBarChart2 className="mr-2" />}>
                      Get Expense Report
                    </Button>
                  </div>
                </Card>
                
                {/* Consumption Level Card */}
                <Card variant="glass" className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Consumption Level</h2>
                  
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Current Level</span>
                      <span className="text-sm text-primary font-medium">Moderate</span>
                    </div>
                    <div className="w-full h-2 bg-border rounded-full">
                      <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full" style={{ width: '65%' }}></div>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-text/50">Low</span>
                      <span className="text-xs text-text/50">High</span>
                    </div>
                  </div>
                  
                  <p className="text-text/70 text-sm mb-4">
                    Based on your income level, you're maintaining a moderate consumption level. 
                    This gives you a good balance between enjoying your money and saving for the future.
                  </p>
                  
                  <Link to="/financial-intelligence">
                    <Button size="sm" variant="ghost" rightIcon={<FiArrowRight size={16} />}>
                      View Detailed Analysis
                    </Button>
                  </Link>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'habits' && (
            <div className="space-y-6">
              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-6">Spending Habits & Patterns</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {spendingHabits.map((habit) => (
                    <div key={habit.id} className="p-4 border border-border rounded-lg bg-card/60">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-medium">{habit.title}</h3>
                          <p className="text-sm text-text/70">{habit.frequency}</p>
                        </div>
                        <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                          ${habit.amount}/mo
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiBarChart2 className="text-secondary" size={16} />
                        </div>
                        <div>
                          <p className="text-text/70">AI Insight:</p>
                          <p className="font-medium">{habit.insight}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-4">Behavioral Analysis</h3>
                  
                  <div className="p-4 border border-border rounded-lg bg-card/60">
                    <p className="mb-4">
                      Our AI has analyzed your spending patterns and identified the following habits:
                    </p>
                    
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiTrendingUp className="text-primary" size={14} />
                        </div>
                        <p>You tend to spend more on weekends, particularly on dining and entertainment.</p>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiTrendingUp className="text-secondary" size={14} />
                        </div>
                        <p>Your grocery shopping is most cost-effective when done weekly on Sundays.</p>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiTrendingUp className="text-accent" size={14} />
                        </div>
                        <p>You frequently make small impulse purchases ($5-15) on workdays.</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="space-y-6">
              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-6">Spending Trends & Predictions</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {trends.map((trend) => (
                    <div key={trend.id} className="p-4 border border-border rounded-lg bg-card/60">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-medium">{trend.title}</h3>
                        <div className={`px-3 py-1 rounded-full ${trend.isPositive ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'} text-sm font-medium`}>
                          {trend.change > 0 ? '+' : ''}{trend.change}%
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiTrendingUp className="text-primary" size={16} />
                        </div>
                        <div>
                          <p className="text-text/70">AI Insight:</p>
                          <p className="font-medium">{trend.insight}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-4">AI Predictions</h3>
                  
                  <div className="p-4 border border-border rounded-lg bg-card/60">
                    <p className="mb-4">
                      Based on your historical data and current patterns, our AI predicts the following for the next 3 months:
                    </p>
                    
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiCalendar className="text-primary" size={14} />
                        </div>
                        <div>
                          <p className="font-medium">July - Expected Spending: $3,150</p>
                          <p className="text-text/70 text-sm">5% increase due to summer activities and vacation planning</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiCalendar className="text-secondary" size={14} />
                        </div>
                        <div>
                          <p className="font-medium">August - Expected Spending: $3,300</p>
                          <p className="text-text/70 text-sm">10% increase due to vacation expenses and back-to-school shopping</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center mr-3 mt-0.5">
                          <FiCalendar className="text-accent" size={14} />
                        </div>
                        <div>
                          <p className="font-medium">September - Expected Spending: $3,000</p>
                          <p className="text-text/70 text-sm">Return to average spending patterns</p>
                        </div>
                      </li>
                    </ul>
                    
                    <div className="mt-4">
                      <Button variant="outline" size="sm">Get Detailed Forecast</Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ConsumptionInsights; 